// ==============================================================================
// Consumable Sub-Warehouse Workflow & Transfer Helper
// Dental Fang Hospital CSSD
// ==============================================================================

import { generateConsumableUID } from './helpers';

const LOCAL_STORAGE_KEY = 'dental_consumable_transfers';

/**
 * Safely read transfers from localStorage
 */
export const getLocalStorageTransfers = () => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn('Failed to parse transfers from localStorage:', err);
    return [];
  }
};

/**
 * Safely save transfers to localStorage
 */
export const saveLocalStorageTransfers = (transfers) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(transfers));
  } catch (err) {
    console.warn('Failed to save transfers to localStorage:', err);
  }
};

/**
 * Helper to generate unique transfer tracking number
 */
export const generateTransferNumber = (prefix = 'TRF') => {
  const d = new Date();
  const yy = d.getFullYear().toString().slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const rand = Math.floor(100 + Math.random() * 900);
  return `${prefix}-${yy}${mm}${dd}-${hh}${min}${rand}`;
};

/**
 * 1. fetchConsumableTransfers
 * Fetches all rows from consumable_transfers ordered by created_at desc.
 * Supports both direct client: fetchConsumableTransfers(supabase)
 * and options object: fetchConsumableTransfers({ supabase, status, destination_warehouse, source_warehouse })
 * Gracefully falls back to localStorage & logs, NEVER throws or crashes.
 */
export async function fetchConsumableTransfers(supabaseOrOptions, options = {}) {
  const localList = getLocalStorageTransfers();

  let supabase = null;
  let status = null;
  let destination_warehouse = null;
  let source_warehouse = null;

  if (supabaseOrOptions && typeof supabaseOrOptions.from === 'function') {
    supabase = supabaseOrOptions;
    status = options.status || null;
    destination_warehouse = options.destination_warehouse || null;
    source_warehouse = options.source_warehouse || null;
  } else if (supabaseOrOptions && typeof supabaseOrOptions === 'object') {
    supabase = supabaseOrOptions.supabase || null;
    status = supabaseOrOptions.status || null;
    destination_warehouse = supabaseOrOptions.destination_warehouse || null;
    source_warehouse = supabaseOrOptions.source_warehouse || null;
  }

  if (!supabase) {
    return localList;
  }

  try {
    let query = supabase
      .from('consumable_transfers')
      .select('*')
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }
    if (destination_warehouse) {
      query = query.eq('destination_warehouse', destination_warehouse);
    }
    if (source_warehouse) {
      query = query.eq('source_warehouse', source_warehouse);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('consumable_transfers query notice (falling back to local cache):', error.message || error);
      return localList;
    }

    if (Array.isArray(data)) {
      const serverIds = new Set(data.map(d => String(d.id || d.transfer_number)));
      const unSynced = localList.filter(l => !serverIds.has(String(l.id || l.transfer_number)));
      const combined = [...unSynced, ...data];
      saveLocalStorageTransfers(combined);
      return combined;
    }

    return localList;
  } catch (err) {
    console.warn('fetchConsumableTransfers fallback triggered:', err);
    return localList;
  }
}

/**
 * 2. createMainToSubTransfer
 * - Cuts stock in `supplies` at `sourceWarehouse` ('คลังใหญ่ (คลังพัสดุ)')
 * - Does NOT increment `destinationWarehouse` yet!
 * - Inserts row into `consumable_transfers` with status 'pending_sub_receipt'
 * - Syncs to localStorage and writes audit log to `logs` table
 * - Returns { success: true, transferNumber, transferRow }
 */
export const createMainToSubTransfer = async ({
  supabase,
  masterItem,
  sourceWarehouse = 'คลังใหญ่ (คลังพัสดุ)',
  destinationWarehouse = 'คลังย่อย (คลังห้องฟัน)',
  quantity = 1,
  transferredBy = 'Admin',
  note = ''
}) => {
  const numQty = Number(quantity);
  if (isNaN(numQty) || numQty <= 0) {
    throw new Error('จำนวนที่โอนต้องมากกว่า 0');
  }

  const masterId = masterItem?.id;
  if (!masterId) {
    throw new Error('ไม่พบรหัสวัสดุสิ้นเปลือง (masterId)');
  }

  const nowIso = new Date().toISOString();
  const transferNumber = generateTransferNumber('TRF');

  // 1. Cut stock from source warehouse in supplies
  if (supabase) {
    const { data: sourceRows, error: fetchErr } = await supabase
      .from('supplies')
      .select('*')
      .eq('master_id', masterId)
      .eq('warehouse_name', sourceWarehouse);

    if (fetchErr) {
      throw new Error(`ตรวจสอบสต็อกคลังต้นทางไม่สำเร็จ: ${fetchErr.message}`);
    }

    const sourceSupply = sourceRows && sourceRows.length > 0 ? sourceRows[0] : null;
    const currentAmount = Number(sourceSupply?.item_amount) || 0;

    if (!sourceSupply || currentAmount < numQty) {
      throw new Error(
        `ยอดคงเหลือใน ${sourceWarehouse} มีเพียง ${currentAmount} ${masterItem?.unit || 'ชิ้น'} ไม่เพียงพอสำหรับการโอน ${numQty} ${masterItem?.unit || 'ชิ้น'}`
      );
    }

    const newAmount = currentAmount - numQty;
    const { error: updateErr } = await supabase
      .from('supplies')
      .update({
        item_amount: newAmount,
        last_updated: nowIso
      })
      .eq('id', sourceSupply.id);

    if (updateErr) {
      throw new Error(`ไม่สามารถตัดสต็อกคลังต้นทางได้: ${updateErr.message}`);
    }
  }

  // 2. Prepare transfer record
  const transferRow = {
    id: `trans-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    transfer_number: transferNumber,
    master_id: masterId,
    item_name: masterItem?.item_name || '',
    lot_number: masterItem?.lot_number || null,
    exp_date: masterItem?.exp_date || null,
    quantity: numQty,
    unit: masterItem?.unit || 'ชิ้น',
    source_warehouse: sourceWarehouse,
    destination_warehouse: destinationWarehouse,
    status: 'pending_sub_receipt',
    transferred_by: transferredBy,
    transferred_at: nowIso,
    received_by: null,
    received_at: null,
    approved_by: null,
    approved_at: null,
    approval_notes: note ? note.trim() : null,
    qr_codes: [],
    created_at: nowIso
  };

  // 3. Try insert into consumable_transfers in Supabase
  if (supabase) {
    try {
      const { data: insertedData, error: insertErr } = await supabase
        .from('consumable_transfers')
        .insert([{
          transfer_number: transferNumber,
          master_id: masterId,
          item_name: transferRow.item_name,
          lot_number: transferRow.lot_number,
          exp_date: transferRow.exp_date,
          quantity: transferRow.quantity,
          unit: transferRow.unit,
          source_warehouse: transferRow.source_warehouse,
          destination_warehouse: transferRow.destination_warehouse,
          status: 'pending_sub_receipt',
          transferred_by: transferRow.transferred_by,
          transferred_at: transferRow.transferred_at,
          approval_notes: transferRow.approval_notes,
          qr_codes: transferRow.qr_codes
        }])
        .select();

      if (!insertErr && insertedData && insertedData.length > 0) {
        transferRow.id = insertedData[0].id;
      } else if (insertErr) {
        console.warn('consumable_transfers insert notice (using local cache):', insertErr.message);
      }
    } catch (dbErr) {
      console.warn('Failed to insert into consumable_transfers table:', dbErr);
    }
  }

  // 4. Sync to localStorage
  const localList = getLocalStorageTransfers();
  const updatedLocal = [transferRow, ...localList];
  saveLocalStorageTransfers(updatedLocal);

  // 5. Write audit log to logs table
  if (supabase) {
    const lotNumber = masterItem?.lot_number || '-';
    const noteSuffix = note && note.trim() ? ` [หมายเหตุ: ${note.trim()}]` : '';
    const actionLog = `โอนย้าย (รอรับเข้าคลังย่อย) จาก ${sourceWarehouse} ไปยัง ${destinationWarehouse} จำนวน ${numQty} ${masterItem?.unit || 'ชิ้น'} (Lot: ${lotNumber}) โดย ${transferredBy}${noteSuffix}`;

    try {
      await supabase.from('logs').insert([{
        item_name: masterItem?.item_name || '',
        warehouse: sourceWarehouse,
        action: actionLog
      }]);
    } catch (logErr) {
      console.warn('บันทึก log ไม่สำเร็จ:', logErr);
    }
  }

  return {
    success: true,
    transferNumber,
    transferRow
  };
};

/**
 * createConsumableTransfer (compatibility alias/function)
 */
export async function createConsumableTransfer({
  supabase,
  masterId,
  itemName,
  lotNumber,
  expDate,
  quantity,
  unit,
  sourceWarehouse = 'คลังใหญ่ (คลังพัสดุ)',
  destinationWarehouse = 'คลังย่อย (คลังห้องฟัน)',
  status = 'pending_sub_receipt',
  sentBy = 'ผู้ส่งคลังใหญ่',
  notes = ''
}) {
  return createMainToSubTransfer({
    supabase,
    masterItem: {
      id: masterId,
      item_name: itemName,
      lot_number: lotNumber,
      exp_date: expDate,
      unit
    },
    sourceWarehouse,
    destinationWarehouse,
    quantity,
    transferredBy: sentBy,
    note: notes
  });
}

/**
 * 3. confirmSubWarehouseReceipt
 * - Increments stock in `supplies` at `คลังย่อย (คลังห้องฟัน)` for `masterId`
 * - Updates `consumable_transfers` row status to 'received', received_by, received_at
 * - Writes audit log to `logs` table
 * - Returns { success: true }
 */
export async function confirmSubWarehouseReceipt({
  supabase,
  transferId,
  masterId: directMasterId,
  transfer,
  receivedQuantity,
  quantity,
  receivedBy,
  receiverName,
  note = '',
  notes = '',
  supplies = [],
  fetchData = null
}) {
  const masterId = directMasterId || transfer?.master_id;
  const numQty = Number(receivedQuantity ?? quantity ?? transfer?.quantity) || 1;
  const actor = receivedBy || receiverName || 'ผู้ดูแลคลังย่อย';
  const finalNote = note || notes || '';
  const nowIso = new Date().toISOString();
  const subWarehouseName = 'คลังย่อย (คลังห้องฟัน)';

  // 1. Increment or insert supply at sub warehouse
  if (supabase && masterId) {
    const { data: destRows, error: fetchErr } = await supabase
      .from('supplies')
      .select('*')
      .eq('master_id', masterId)
      .eq('warehouse_name', subWarehouseName);

    if (fetchErr) {
      console.warn(`ตรวจสอบสต็อกคลังย่อย: ${fetchErr.message}`);
    }

    const subSupply = destRows && destRows.length > 0 ? destRows[0] : null;

    if (subSupply) {
      const newAmount = (Number(subSupply.item_amount) || 0) + numQty;
      const { error: updateErr } = await supabase
        .from('supplies')
        .update({
          item_amount: newAmount,
          last_updated: nowIso
        })
        .eq('id', subSupply.id);

      if (updateErr) {
        throw new Error(`ไม่สามารถเพิ่มสต็อกในคลังย่อยได้: ${updateErr.message}`);
      }
    } else {
      const { error: insertErr } = await supabase
        .from('supplies')
        .insert([{
          master_id: masterId,
          warehouse_name: subWarehouseName,
          item_amount: numQty,
          last_updated: nowIso
        }]);

      if (insertErr) {
        throw new Error(`ไม่สามารถสร้างรายการสต็อกในคลังย่อยได้: ${insertErr.message}`);
      }
    }
  }

  // 2. Update consumable_transfers status in Supabase
  if (supabase && transferId) {
    try {
      await supabase
        .from('consumable_transfers')
        .update({
          status: 'received',
          received_by: actor,
          received_at: nowIso,
          approval_notes: finalNote ? `${transfer?.approval_notes ? transfer.approval_notes + ' | ' : ''}รับเข้า: ${finalNote}` : transfer?.approval_notes
        })
        .eq('id', transferId);
    } catch (tErr) {
      console.warn('Update transfer status error:', tErr);
    }
  }

  // 3. Update localStorage
  const localList = getLocalStorageTransfers();
  const updatedLocal = localList.map(item => {
    if (String(item.id) === String(transferId) || String(item.transfer_number) === String(transferId)) {
      return {
        ...item,
        status: 'received',
        received_by: actor,
        received_at: nowIso
      };
    }
    return item;
  });
  saveLocalStorageTransfers(updatedLocal);

  // 4. Write audit log to logs table
  if (supabase) {
    const noteSuffix = finalNote && finalNote.trim() ? ` [หมายเหตุ: ${finalNote.trim()}]` : '';
    const actionLog = `ยืนยันรับของเข้าคลังย่อย (คลังห้องฟัน) จำนวน ${numQty} โดย ${actor}${noteSuffix}`;

    try {
      await supabase.from('logs').insert([{
        item_name: transfer?.item_name || `Master ID #${masterId}`,
        warehouse: subWarehouseName,
        action: actionLog
      }]);
    } catch (logErr) {
      console.warn('บันทึก log ไม่สำเร็จ:', logErr);
    }
  }

  if (typeof fetchData === 'function') {
    try {
      await fetchData();
    } catch (e) {
      console.warn('fetchData callback error:', e);
    }
  }

  return {
    success: true,
    message: `รับของเข้าคลังย่อยสำเร็จ จำนวน ${numQty}`
  };
}

/**
 * 4. generateAndTagSubWarehouseQR
 * - Generates `count` unique QR codes using `generateConsumableUID(i)` from helpers.js
 * - Inserts rows into `consumable_items` table with current_location: 'คลังย่อย (คลังห้องฟัน)', status: 'active', caretaker_name: caretakerName, label_type: labelType
 * - Inserts audit rows into `consumable_logs` table
 * - If `transferId` provided, updates `qr_codes` array in `consumable_transfers`
 * - Returns generated array of item objects
 */
export const generateAndTagSubWarehouseQR = async ({
  supabase,
  transferId = null,
  masterItem,
  count = 1,
  labelType = 'full',
  caretakerName = 'ผู้ดูแลคลังย่อย'
}) => {
  const numCount = Math.max(1, parseInt(count, 10) || 1);
  const nowIso = new Date().toISOString();
  const subWarehouseName = 'คลังย่อย (คลังห้องฟัน)';

  const generatedQRs = [];
  const consumableRows = [];
  const logRows = [];

  for (let i = 0; i < numCount; i++) {
    const uid = generateConsumableUID(i);
    generatedQRs.push(uid);

    const itemObj = {
      qr_code: uid,
      master_id: masterItem?.id || null,
      item_name: masterItem?.item_name || 'วัสดุสิ้นเปลือง',
      specialty: masterItem?.specialty || null,
      lot_number: masterItem?.lot_number || null,
      exp_date: masterItem?.exp_date || null,
      mfg_date: masterItem?.mfg_date || null,
      unit: masterItem?.unit || 'ชิ้น',
      unit_price: Number(masterItem?.unit_price) || 0,
      total_price: Number(masterItem?.unit_price) || 0,
      caretaker_name: caretakerName,
      current_location: subWarehouseName,
      status: 'active',
      label_type: labelType,
      created_by: caretakerName,
      created_at: nowIso
    };

    consumableRows.push(itemObj);

    logRows.push({
      qr_code: uid,
      action: `สร้างฉลาก QR ติดแท็กเข้าคลังย่อย (${labelType === 'compact' ? 'แบบย่อ' : 'แบบเต็ม'})`,
      location: subWarehouseName,
      actor_name: caretakerName,
      notes: `Lot: ${masterItem?.lot_number || '-'}, EXP: ${masterItem?.exp_date || '-'}`
    });
  }

  // Insert into consumable_items
  if (supabase) {
    try {
      const { error: insertErr } = await supabase
        .from('consumable_items')
        .insert(consumableRows);

      if (insertErr) {
        console.warn('consumable_items insert notice:', insertErr.message);
      }
    } catch (err) {
      console.warn('Error inserting into consumable_items:', err);
    }

    // Insert into consumable_logs
    try {
      const { error: logErr } = await supabase
        .from('consumable_logs')
        .insert(logRows);

      if (logErr) {
        console.warn('consumable_logs insert notice:', logErr.message);
      }
    } catch (err) {
      console.warn('Error inserting into consumable_logs:', err);
    }

    // Update transfer qr_codes if transferId provided
    if (transferId) {
      try {
        const { data: currentTransfer } = await supabase
          .from('consumable_transfers')
          .select('qr_codes')
          .eq('id', transferId)
          .single();

        const existingQrs = Array.isArray(currentTransfer?.qr_codes) ? currentTransfer.qr_codes : [];
        const mergedQrs = Array.from(new Set([...existingQrs, ...generatedQRs]));

        await supabase
          .from('consumable_transfers')
          .update({ qr_codes: mergedQrs })
          .eq('id', transferId);
      } catch (updateErr) {
        console.warn('Failed to update transfer qr_codes:', updateErr);
      }
    }
  }

  // Also sync to localStorage transfer record if transferId exists
  if (transferId) {
    const localList = getLocalStorageTransfers();
    const updatedLocal = localList.map(item => {
      if (String(item.id) === String(transferId) || String(item.transfer_number) === String(transferId)) {
        const existingQrs = Array.isArray(item.qr_codes) ? item.qr_codes : [];
        return {
          ...item,
          qr_codes: Array.from(new Set([...existingQrs, ...generatedQRs]))
        };
      }
      return item;
    });
    saveLocalStorageTransfers(updatedLocal);
  }

  return consumableRows;
};

/**
 * 5. approveAndDispenseFromSub
 * - Decrements stock in `supplies` at `คลังย่อย (คลังห้องฟัน)`
 * - Increments stock at `destinationWarehouse` ('ห้องเก็บของ' หรือ 'ห้องคลีนิก')
 * - If `qrCodesToDispense` provided, updates `consumable_items` to destinationWarehouse & status
 * - Inserts row into `consumable_transfers` with status 'approved_outbound', approved_by, approved_at
 * - Writes audit log to `logs` table with caretaker approval record
 * - Returns { success: true }
 */
export async function approveAndDispenseFromSub({
  supabase,
  masterItem,
  masterId: directMasterId,
  itemName: directItemName,
  sourceWarehouse = 'คลังย่อย (คลังห้องฟัน)',
  destinationWarehouse,
  quantity = 1,
  lotNumber: directLotNumber = '',
  unit: directUnit = 'ชิ้น',
  approvedBy,
  caretakerName,
  note = '',
  notes = '',
  qrCodesToDispense,
  qrCodes,
  fetchData = null
}) {
  const masterId = masterItem?.id || directMasterId;
  const itemName = masterItem?.item_name || directItemName || '';
  const lotNumber = masterItem?.lot_number || directLotNumber || '';
  const unit = masterItem?.unit || directUnit || 'ชิ้น';
  const approver = approvedBy || caretakerName || 'ผู้ดูแลคลังย่อย';
  const finalNote = note || notes || '';
  const finalQRs = qrCodesToDispense || qrCodes || [];

  const numQty = Number(quantity);
  if (isNaN(numQty) || numQty <= 0) {
    throw new Error('จำนวนที่จ่ายออกต้องมากกว่า 0');
  }

  if (!masterId) {
    throw new Error('ไม่พบรหัสวัสดุสิ้นเปลือง (masterId)');
  }

  if (!destinationWarehouse) {
    throw new Error('กรุณาระบุคลังปลายทางที่ต้องการจ่ายไป');
  }

  const subWarehouseName = 'คลังย่อย (คลังห้องฟัน)';
  const nowIso = new Date().toISOString();
  const transferNumber = generateTransferNumber('DSP');

  if (supabase) {
    // 1. Decrement stock from sub-warehouse
    const { data: subRows, error: subFetchErr } = await supabase
      .from('supplies')
      .select('*')
      .eq('master_id', masterId)
      .eq('warehouse_name', subWarehouseName);

    if (subFetchErr) {
      throw new Error(`ตรวจสอบสต็อกคลังย่อยไม่สำเร็จ: ${subFetchErr.message}`);
    }

    const subSupply = subRows && subRows.length > 0 ? subRows[0] : null;
    const currentSubAmount = Number(subSupply?.item_amount) || 0;

    if (!subSupply || currentSubAmount < numQty) {
      throw new Error(
        `ยอดคงเหลือในคลังย่อยมีเพียง ${currentSubAmount} ${unit} ไม่เพียงพอสำหรับการจ่ายออก ${numQty} ${unit}`
      );
    }

    const newSubAmount = currentSubAmount - numQty;
    const { error: subUpdateErr } = await supabase
      .from('supplies')
      .update({
        item_amount: newSubAmount,
        last_updated: nowIso
      })
      .eq('id', subSupply.id);

    if (subUpdateErr) {
      throw new Error(`ไม่สามารถตัดสต็อกคลังย่อยได้: ${subUpdateErr.message}`);
    }

    // 2. Increment stock at destination warehouse
    const { data: destRows, error: destFetchErr } = await supabase
      .from('supplies')
      .select('*')
      .eq('master_id', masterId)
      .eq('warehouse_name', destinationWarehouse);

    if (destFetchErr) {
      console.warn(`ตรวจสอบสต็อกปลายทาง: ${destFetchErr.message}`);
    }

    const destSupply = destRows && destRows.length > 0 ? destRows[0] : null;

    if (destSupply) {
      const newDestAmount = (Number(destSupply.item_amount) || 0) + numQty;
      const { error: destUpdateErr } = await supabase
        .from('supplies')
        .update({
          item_amount: newDestAmount,
          last_updated: nowIso
        })
        .eq('id', destSupply.id);

      if (destUpdateErr) {
        throw new Error(`ไม่สามารถเพิ่มสต็อกปลายทางได้: ${destUpdateErr.message}`);
      }
    } else {
      const { error: destInsertErr } = await supabase
        .from('supplies')
        .insert([{
          master_id: masterId,
          warehouse_name: destinationWarehouse,
          item_amount: numQty,
          last_updated: nowIso
        }]);

      if (destInsertErr) {
        throw new Error(`ไม่สามารถสร้างรายการสต็อกปลายทางได้: ${destInsertErr.message}`);
      }
    }

    // 3. Update consumable_items if QR codes provided
    if (Array.isArray(finalQRs) && finalQRs.length > 0) {
      const newStatus = destinationWarehouse.includes('คลีนิก') || destinationWarehouse.includes('ห้อง') ? 'in-use' : 'active';
      try {
        await supabase
          .from('consumable_items')
          .update({
            current_location: destinationWarehouse,
            status: newStatus,
            last_scanned_at: nowIso
          })
          .in('qr_code', finalQRs);

        // Audit in consumable_logs
        const itemLogRows = finalQRs.map(code => ({
          qr_code: code,
          action: `อนุมัติจ่ายออกจากคลังย่อยไปยัง ${destinationWarehouse} โดย ${approver}`,
          location: destinationWarehouse,
          actor_name: approver,
          notes: finalNote || ''
        }));
        await supabase.from('consumable_logs').insert(itemLogRows);
      } catch (qrErr) {
        console.warn('Update consumable_items QR error:', qrErr);
      }
    }
  }

  // 4. Create transfer record with approved_outbound status
  const transferRow = {
    id: `disp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    transfer_number: transferNumber,
    master_id: masterId,
    item_name: itemName,
    lot_number: lotNumber || null,
    exp_date: masterItem?.exp_date || null,
    quantity: numQty,
    unit: unit,
    source_warehouse: subWarehouseName,
    destination_warehouse: destinationWarehouse,
    status: 'approved_outbound',
    transferred_by: approver,
    transferred_at: nowIso,
    received_by: destinationWarehouse,
    received_at: null,
    approved_by: approver,
    approved_at: nowIso,
    approval_notes: finalNote ? finalNote.trim() : null,
    qr_codes: finalQRs,
    created_at: nowIso
  };

  if (supabase) {
    try {
      const { data: insertedTransfer } = await supabase
        .from('consumable_transfers')
        .insert([{
          transfer_number: transferNumber,
          master_id: masterId,
          item_name: transferRow.item_name,
          lot_number: transferRow.lot_number,
          exp_date: transferRow.exp_date,
          quantity: transferRow.quantity,
          unit: transferRow.unit,
          source_warehouse: subWarehouseName,
          destination_warehouse: destinationWarehouse,
          status: 'approved_outbound',
          transferred_by: approver,
          transferred_at: nowIso,
          approved_by: approver,
          approved_at: nowIso,
          approval_notes: transferRow.approval_notes,
          qr_codes: transferRow.qr_codes
        }])
        .select();

      if (insertedTransfer && insertedTransfer.length > 0) {
        transferRow.id = insertedTransfer[0].id;
      }
    } catch (insertErr) {
      console.warn('consumable_transfers insert notice (using local cache):', insertErr);
    }
  }

  // 5. Sync to localStorage
  const localList = getLocalStorageTransfers();
  const updatedLocal = [transferRow, ...localList];
  saveLocalStorageTransfers(updatedLocal);

  // 6. Write audit log to logs table
  if (supabase) {
    const noteSuffix = finalNote && finalNote.trim() ? ` [หมายเหตุ: ${finalNote.trim()}]` : '';
    const actionLog = `อนุมัติและจ่ายของออกจากคลังย่อย (คลังห้องฟัน) ไปยัง ${destinationWarehouse} จำนวน ${numQty} ${unit} (Lot: ${lotNumber || '-'}) อนุมัติโดย ${approver}${noteSuffix}`;

    try {
      await supabase.from('logs').insert([{
        item_name: itemName,
        warehouse: destinationWarehouse,
        action: actionLog
      }]);
    } catch (logErr) {
      console.warn('บันทึก log ไม่สำเร็จ:', logErr);
    }
  }

  if (typeof fetchData === 'function') {
    try {
      await fetchData();
    } catch (e) {
      console.warn('fetchData callback error:', e);
    }
  }

  return {
    success: true,
    transferNumber,
    message: `อนุมัติตัดของสำเร็จ: เบิก ${itemName} จำนวน ${numQty} ${unit} สู่ ${destinationWarehouse}`
  };
}

/**
 * 6. buildWarehouseStockReportData
 * Calculates inventory balance, valuation, expiry status, and movement summaries
 * for either 'คลังใหญ่' or 'คลังย่อย' (or any warehouse name).
 */
export const buildWarehouseStockReportData = ({
  itemMaster = [],
  supplies = [],
  logs = [],
  targetWarehouse = 'คลังใหญ่ (คลังพัสดุ)'
}) => {
  const isMatchWarehouse = (whName) => {
    if (!whName) return false;
    const target = (targetWarehouse || '').trim();
    const source = whName.trim();
    if (target === source) return true;
    if (target.includes('คลังใหญ่') && source.includes('คลังใหญ่')) return true;
    if (target.includes('คลังย่อย') && source.includes('คลังย่อย')) return true;
    return false;
  };

  const today = new Date();
  let totalQuantity = 0;
  let totalValuation = 0;
  let expiredCount = 0;
  let expiring3mCount = 0;
  let expiring1yCount = 0;
  let normalCount = 0;

  // Filter supplies for target warehouse
  const matchingSupplies = (supplies || []).filter(s => isMatchWarehouse(s.warehouse_name));

  // Map each supply to full item details from itemMaster
  const reportItems = matchingSupplies.map(supply => {
    const master = (itemMaster || []).find(m => String(m.id) === String(supply.master_id)) || {};
    const qty = Number(supply.item_amount) || 0;
    const unitPrice = Number(master.unit_price) || 0;
    const itemTotalValuation = qty * unitPrice;

    totalQuantity += qty;
    totalValuation += itemTotalValuation;

    // Calculate expiry status
    const expDate = master.exp_date;
    let expiryStatus = 'normal';

    if (!expDate || expDate === '9999-12-31') {
      expiryStatus = 'normal';
      normalCount++;
    } else {
      const expire = new Date(expDate);
      const diffMonths = (expire - today) / (1000 * 60 * 60 * 24 * 30);
      if (expire <= today) {
        expiryStatus = 'expired';
        expiredCount++;
      } else if (diffMonths < 3) {
        expiryStatus = 'expiring_soon_3m';
        expiring3mCount++;
      } else if (diffMonths <= 12) {
        expiryStatus = 'expiring_soon_1y';
        expiring1yCount++;
      } else {
        expiryStatus = 'normal';
        normalCount++;
      }
    }

    return {
      supply_id: supply.id,
      master_id: master.id || supply.master_id,
      item_name: master.item_name || 'ไม่ระบุชื่อ',
      lot_number: master.lot_number || '-',
      exp_date: master.exp_date || null,
      unit: master.unit || 'ชิ้น',
      quantity: qty,
      unit_price: unitPrice,
      total_price: itemTotalValuation,
      specialty: master.specialty || null,
      expiryStatus,
      warehouse_name: supply.warehouse_name
    };
  });

  // Filter movements/logs relevant to target warehouse
  const relevantMovements = (logs || []).filter(log => {
    return isMatchWarehouse(log.warehouse) ||
      (log.action && (
        (targetWarehouse.includes('คลังใหญ่') && log.action.includes('คลังใหญ่')) ||
        (targetWarehouse.includes('คลังย่อย') && log.action.includes('คลังย่อย'))
      ));
  });

  return {
    warehouseName: targetWarehouse,
    totalItems: reportItems.length,
    totalQuantity,
    totalValuation,
    items: reportItems,
    expirySummary: {
      expiredCount,
      expiring3mCount,
      expiring1yCount,
      normalCount
    },
    recentMovements: relevantMovements.slice(0, 50)
  };
};

/**
 * Fetch individual consumable items from `consumable_items` table
 */
export async function fetchConsumableQRItems({
  supabase,
  location = null,
  status = null,
  qrCode = null,
  limit = 200
} = {}) {
  if (!supabase) return [];

  try {
    let query = supabase
      .from('consumable_items')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (location) {
      query = query.eq('current_location', location);
    }
    if (status) {
      query = query.eq('status', status);
    }
    if (qrCode) {
      query = query.eq('qr_code', qrCode);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('fetchConsumableQRItems query warning:', error.message || error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('fetchConsumableQRItems error:', err);
    return [];
  }
}

/**
 * Update consumable item status and log audit trail
 */
export async function updateConsumableItemStatus({
  supabase,
  qrCode,
  newStatus,
  newLocation = null,
  actorName = 'ผู้ดูแลคลังย่อย',
  notes = ''
}) {
  if (!supabase || !qrCode) {
    throw new Error('ข้อมูลไม่ครบถ้วน');
  }

  const updates = {
    status: newStatus,
    last_scanned_at: new Date().toISOString()
  };
  if (newLocation) {
    updates.current_location = newLocation;
  }

  const { error: updateErr } = await supabase
    .from('consumable_items')
    .update(updates)
    .eq('qr_code', qrCode);

  if (updateErr) {
    throw new Error(`อัปเดตสถานะ QR ไม่สำเร็จ: ${updateErr.message}`);
  }

  // Insert log
  try {
    await supabase
      .from('consumable_logs')
      .insert([{
        qr_code: qrCode,
        action: `เปลี่ยนสถานะเป็น '${newStatus}'${newLocation ? ` ที่ ${newLocation}` : ''}`,
        location: newLocation,
        actor_name: actorName,
        notes: notes || 'ปรับปรุงสถานะผ่านหน้าจัดการคลังย่อย',
        timestamp: new Date().toISOString()
      }]);
  } catch (logErr) {
    console.warn('consumable_logs insert error:', logErr);
  }

  return { success: true };
}
